import { GasMeterBrand, GasMeterStatus, GasMeterType, GasMeterTypeBrandRelation } from './../../models/gas-meter-catalog.interface';
import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter, signal, inject, model, effect } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { SettlementsService } from 'src/app/services/settlements.service';
import { faEdit, faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { forkJoin, map } from 'rxjs';
import { IconsService } from 'src/app/shared/services/icons.service';
import { GasMeterApprovalsService } from 'src/app/services/gas-meter-approvals.service';
import { MeterSerialNumberValidator } from 'src/app/shared/validators/meter-serialnumber';
import { MeterQrCodeValidator } from 'src/app/shared/validators/meter-qrcode';

@Component({
  selector: 'app-form-gas-meter',
  template: `
    <form [formGroup]="meterForm" (ngSubmit)="onSubmit()">
      <div class="form-group required">
        <label for="name" class="form-label">Nombre</label>
        <input type="text" formControlName="name" class="form-control" id="name" placeholder="">
        <div *ngIf="ufc['name'].touched && ufc['name'].invalid">
          <div *ngIf="ufc['name'].errors?.['required']" class="text-danger text-sm">El nombre es requerido.</div>
        </div>
      </div>

      <div class="form-group required mt-3">
        <label for="meter_code" class="form-label">Clave QR</label>
        <input type="text" formControlName="meter_code" class="form-control" id="meter_code" placeholder="">
        <div *ngIf="ufc['meter_code'].touched && ufc['meter_code'].invalid">
          <div *ngIf="ufc['meter_code'].errors?.['required']" class="text-danger text-sm">La clave es requerida</div>
          <div *ngIf="ufc['meter_code'].errors?.['qrCodeExist']" class="text-danger text-sm">El código QR ya existe</div>
        </div>
      </div>

      <div class="form-group required mt-3">
        <label for="meter_type" class="form-label">Tipo de medidor</label>
        <select formControlName="gas_meter_type_id" class="form-select" id="meter_type">
          <option [ngValue]="null">Selecciona un tipo</option>
          @for (type of availableMeterTypes(); track type.id) {
            <option [ngValue]="toNumber(type.id)">{{ type.description }}</option>
          }
        </select>
        <div *ngIf="ufc['gas_meter_type_id'].touched && ufc['gas_meter_type_id'].invalid">
          <div *ngIf="ufc['gas_meter_type_id'].errors?.['required']" class="text-danger text-sm">El tipo de medidor es requerido</div>
        </div>
      </div>

      <div class="form-group required mt-3">
        <label for="meter_status" class="form-label">Estatus</label>
        <select formControlName="gas_meter_status_id" class="form-select" id="meter_status">
          <option [ngValue]="null">Selecciona un estatus</option>
          @for (status of editableMeterStatuses(); track status.id) {
            <option [ngValue]="toNumber(status.id)">{{ status.description }}</option>
          }
        </select>
        <div *ngIf="ufc['gas_meter_status_id'].touched && ufc['gas_meter_status_id'].invalid">
          <div *ngIf="ufc['gas_meter_status_id'].errors?.['required']" class="text-danger text-sm">El estatus es requerido</div>
        </div>
      </div>

      <div class="form-group required mt-3">
        <label for="meter_brand" class="form-label">Marca del medidor</label>
        <select formControlName="gas_meter_brand_id" class="form-select" id="meter_brand">
          <option [ngValue]="null">Selecciona una marca</option>
          @for (brand of availableMeterBrands(); track brand.id) {
            <option [ngValue]="toNumber(brand.id)">{{ brand.description }}</option>
          }
        </select>
        <div *ngIf="ufc['gas_meter_brand_id'].touched && ufc['gas_meter_brand_id'].invalid">
          <div *ngIf="ufc['gas_meter_brand_id'].errors?.['required']" class="text-danger text-sm">La marca es requerida</div>
        </div>
        <div *ngIf="!approved() && isTypeBrandCombinationInvalid()" class="text-danger text-sm mt-1">La combinación tipo/marca no es válida.</div>
      </div>

      <div class="form-group required mt-3">
        <label for="meter_serial" class="form-label">Número de serie</label>
        <input type="text" formControlName="meter_serial" class="form-control" id="meter_serial" placeholder="">
        <div *ngIf="ufc['meter_serial'].touched && ufc['meter_serial'].invalid">
          <div *ngIf="ufc['meter_serial'].errors?.['required']" class="text-danger text-sm">El número de serie es requerido.</div>
          <div *ngIf="ufc['meter_serial'].errors?.['serialExist']" class="text-danger text-sm">El número de serie ya existe</div>
        </div>
      </div>

      <div class="last-section">
        <div class="left">
          <div class="form-group required mt-3">
            <label for="last_read" class="form-label">Última lectura</label>
            <input
              type="text"
              inputmode="decimal"
              formControlName="last_read"
              class="form-control"
              id="last_read"
              (blur)="formatLastReadField()"
              placeholder=""
            >
            <div *ngIf="selectedReadingFormat()" class="text-muted text-sm mt-1">Formato esperado: {{ readingFormatLabel() }}</div>
            <div *ngIf="ufc['last_read'].touched && ufc['last_read'].invalid">
              <div *ngIf="ufc['last_read'].errors?.['required']" class="text-danger text-sm">La última lectura es requerida.</div>
              <div *ngIf="ufc['last_read'].errors?.['readingFormat']" class="text-danger text-sm">
                La lectura debe cumplir el formato {{ selectedReadingFormat() }}.
              </div>
            </div>
          </div>

          <div class="form-group required mt-4">
            <div class="form-check form-switch ps-1">
              <input
                class="form-check-input float-end"
                type="checkbox"
                id="comodato"
                formControlName="comodato"
              >
              <label class="form-check-label" for="comodato">
                ¿Medidor en comodato?
              </label>
            </div>
          </div>
          <app-gas-meter-approvals [(approved)]="approved" [meterId]="meter?.id ?? ''"></app-gas-meter-approvals>
        </div>
        <div class="right" [class.end]="imgEvidence()">
          @if (imgEvidence()) {
            <img [src]="imgEvidence()" alt="evidence-image">
            <button (click)="deleteImage()" [disabled]="approved() !== false || saving" type="button" class="btn btn-zazz-dark ms-2 shadow-sm">Eliminar</button>
          }
          @else {
            <fa-icon [icon]="icons.faImage"></fa-icon>
          }
        </div>
      </div>

      <div class="form-group mt-4 d-flex justify-content-end">
        <button [disabled]="!canSave" type="submit" class="btn btn-outline-success rounded-3">
          <fa-icon [icon]="faCircleNotch" animation="spin" *ngIf="loading || saving" class="ms-2 text-dark"></fa-icon>
          Guardar
        </button>
      </div>
    </form>
  `,
  styleUrls: ['./form-gas-meter.component.scss']
})
export class FormGasMeterComponent implements OnInit, OnChanges {
  @Input() meter: any;
  @Input() unit_id: any;
  @Input() saving = false;
  @Output() updateMeter = new EventEmitter<any>();
  @Output() createMeter = new EventEmitter<any>();
  @Output() cancelMeter = new EventEmitter<any>();

  approved = model<boolean>();
  imgEvidence = signal<string>('');
  meterTypes = signal<GasMeterType[]>([]);
  meterStatuses = signal<GasMeterStatus[]>([]);
  meterBrands = signal<GasMeterBrand[]>([]);
  availableMeterTypes = signal<GasMeterType[]>([]);
  availableMeterBrands = signal<GasMeterBrand[]>([]);
  typeBrandRelations = signal<GasMeterTypeBrandRelation[]>([]);
  selectedReadingFormat = signal<string>('');
  currentMeterId = signal<number | null>(null);

  icons = inject(IconsService);
  gasMeterApprovalService = inject(GasMeterApprovalsService);

  loading = false;
  extraData = false;
  loadingTypeBrandRelations = false;
  meterForm: FormGroup = new FormGroup({});

  faEdit = faEdit;
  faCircleNotch = faCircleNotch;

  private catalogsLoaded = false;
  private pendingMeter: any = null;
  private suppressControlEvents = false;
  private relationRequestId = 0;

  constructor(
    private store: SettlementsService,
    private toastr: ToastrService,
    private fb: FormBuilder
  ) {
    this.buildForm();
    effect(() => {
      const approved = this.approved();
      const waitingForApproval = !!this.currentMeterId() && approved === undefined;
      Object.entries(this.meterForm.controls).forEach(([name, control]) => {
        if (waitingForApproval || (approved && name !== 'gas_meter_status_id')) {
          control.disable({ emitEvent: false });
        } else if (control.disabled) {
          control.enable({ emitEvent: false });
        }
      });
    });
  }

  ngOnInit(): void {
    this.setupTypeBrandWatchers();
    this.getCatalogs();

    if (this.unit_id) {
      this.meterForm.patchValue({ unit_id: this.unit_id }, { emitEvent: false });
    } else {
      this.extraData = true;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['meter']) {
      this.pendingMeter = changes['meter'].currentValue;
      if (this.catalogsLoaded) {
        this.applyMeterData(this.pendingMeter);
      }
    }

    if (changes['unit_id'] && this.unit_id) {
      this.meterForm.patchValue({ unit_id: this.unit_id }, { emitEvent: false });
    }
  }

  getCatalogs() {
    forkJoin({
      types: this.store.getGasMeterTypes(),
      statuses: this.store.getGasMeterStatuses(),
      brands: this.store.getGasMeterBrands()
    }).subscribe({
      next: (res) => {
        this.meterTypes.set(res.types || []);
        this.meterStatuses.set(res.statuses || []);
        this.meterBrands.set(res.brands || []);
        this.availableMeterTypes.set([...(res.types || [])]);
        this.availableMeterBrands.set([...(res.brands || [])]);

        this.catalogsLoaded = true;
        this.setUniqueValidators();

        if (this.pendingMeter) {
          this.applyMeterData(this.pendingMeter);
        } else {
          this.resetFormForCreate();
        }
      },
      error: () => {
        this.toastr.error('No se pudieron cargar los catálogos de medidor', 'Error');
      }
    });
  }

  buildForm() {
    this.meterForm = this.fb.group({
      id: [''],
      name: ['', Validators.required],
      meter_code: ['', Validators.required],
      gas_meter_type_id: [null, Validators.required],
      gas_meter_status_id: [null, Validators.required],
      gas_meter_brand_id: [null, Validators.required],
      gas_meter_type_brand_id: [null, Validators.required],
      meter_serial: ['', Validators.required],
      meter_location_latitude: [''],
      meter_location_longitude: [''],
      last_read: ['', [Validators.required, this.lastReadFormatValidator()]],
      unit_id: [''],
      comodato: [false, Validators.required]
    });
  }

  get ufc() {
    return this.meterForm.controls;
  }

  cancel() {
    this.cancelMeter.emit(null);
  }

  onSubmit() {
    if (!this.canSave) {
      return;
    }

    if (this.approved()) {
      // Approved meters can change status only; never resend their registration data.
      this.updateMeter.emit({
        id: this.currentMeterId(),
        gas_meter_status_id: this.toNumber(this.ufc['gas_meter_status_id'].value)
      });
      return;
    }

    this.loading = true;
    this.meterForm.patchValue({ unit_id: this.unit_id }, { emitEvent: false });

    if (!this.formatLastReadField(false)) {
      this.loading = false;
      this.toastr.error('Error', 'La última lectura no cumple el formato del medidor.');
      this.meterForm.markAllAsTouched();
      return;
    }

    if (this.loadingTypeBrandRelations) {
      this.loading = false;
      this.toastr.error('Espera a que termine la validación de tipo y marca.', 'Error');
      return;
    }

    if (this.meterForm.invalid || this.isTypeBrandCombinationInvalid()) {
      this.loading = false;
      this.toastr.error('Error', 'Formulario incompleto o con combinación inválida.');
      this.meterForm.markAllAsTouched();
      return;
    }

    const payload = this.buildPayload();
    if (!payload) {
      this.loading = false;
      this.toastr.error('No fue posible construir los datos del medidor.', 'Error');
      return;
    }

    if (payload.id) {
      this.updateMeter.emit(payload);
    } else {
      this.createMeter.emit(payload);
    }

    this.loading = false;
  }

  get canSave(): boolean {
    if (!this.catalogsLoaded || this.loading || this.saving ||
        (this.currentMeterId() && this.approved() === undefined)) {
      return false;
    }
    if (!this.approved()) {
      return !this.loadingTypeBrandRelations;
    }
    const statusId = this.toNumber(this.ufc['gas_meter_status_id'].value);
    return !!this.currentMeterId() && statusId !== this.resolveStatusId(this.meter) &&
      this.editableMeterStatuses().some(status => this.toNumber(status.id) === statusId);
  }

  editableMeterStatuses(): GasMeterStatus[] {
    return this.approved()
      ? this.meterStatuses().filter(status =>
          [status.name, status.description].some(value =>
            ['activo', 'inactivo', 'active', 'inactive'].includes(this.normalizeCatalogKey(value))
          )
        )
      : this.meterStatuses();
  }

  readingFormatLabel(): string {
    const parsed = this.parseReadingFormat(this.selectedReadingFormat());
    if (!parsed) {
      return '';
    }

    if (parsed.decimalDigits === 0) {
      return `${parsed.integerDigits} enteros`;
    }

    return `${parsed.integerDigits} enteros y ${parsed.decimalDigits} decimales`;
  }

  isTypeBrandCombinationInvalid(): boolean {
    const typeId = this.toNumber(this.ufc['gas_meter_type_id'].value);
    const brandId = this.toNumber(this.ufc['gas_meter_brand_id'].value);
    const relationId = this.toNumber(this.ufc['gas_meter_type_brand_id'].value);

    return !!typeId && !!brandId && !relationId;
  }

  toNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  setUniqueValidators(currentId = this.currentMeterId()) {
    const serialControl = this.meterForm.controls['meter_serial'];
    const qrControl = this.meterForm.controls['meter_code'];

    [serialControl, qrControl].forEach((control, index) => {
      control.clearAsyncValidators();
      control.setValidators([Validators.required]);
      control.setAsyncValidators([
        index === 0
          ? MeterSerialNumberValidator(this.gasMeterApprovalService, currentId)
          : MeterQrCodeValidator(this.gasMeterApprovalService, currentId)
      ]);
      control.updateValueAndValidity();
    });
  }

  deleteImage() {
    if (this.approved() !== false || this.saving) {
      return;
    }
    if (this.currentMeterId()) {
      this.store.deleteGasMeterImage(this.currentMeterId()!).subscribe({
        next: () => {
          this.toastr.success('Se ha eliminado la imagen');
          this.imgEvidence.set('');
        },
        error: () => {
          this.toastr.error('No se ha podido eliminar la imagen');
        }
      });
    }
  }

  formatLastReadField(showError = true): boolean {
    const currentValue = `${this.ufc['last_read'].value ?? ''}`.trim();
    if (!currentValue) {
      return true;
    }

    const formattedValue = this.formatReadWithMask(currentValue, this.selectedReadingFormat());
    if (!formattedValue) {
      if (showError) {
        this.toastr.error('La última lectura no cumple el formato del medidor.', 'Error');
      }
      this.ufc['last_read'].markAsTouched();
      this.ufc['last_read'].updateValueAndValidity({ emitEvent: false });
      return false;
    }

    this.patchControlValue('last_read', formattedValue);
    return true;
  }

  private setupTypeBrandWatchers() {
    this.ufc['gas_meter_type_id'].valueChanges.subscribe((rawTypeId) => {
      if (this.suppressControlEvents || !this.catalogsLoaded) {
        return;
      }

      const typeId = this.toNumber(rawTypeId);
      const brandId = this.toNumber(this.ufc['gas_meter_brand_id'].value);
      this.syncTypeBrandCatalog(typeId, brandId);
    });

    this.ufc['gas_meter_brand_id'].valueChanges.subscribe((rawBrandId) => {
      if (this.suppressControlEvents || !this.catalogsLoaded) {
        return;
      }

      const typeId = this.toNumber(this.ufc['gas_meter_type_id'].value);
      const brandId = this.toNumber(rawBrandId);
      this.syncTypeBrandCatalog(typeId, brandId);
    });
  }

  private resetFormForCreate(): void {
    this.patchForm(
      {
        id: '',
        name: '',
        meter_code: '',
        gas_meter_type_id: null,
        gas_meter_status_id: null,
        gas_meter_brand_id: null,
        gas_meter_type_brand_id: null,
        meter_serial: '',
        meter_location_latitude: '',
        meter_location_longitude: '',
        last_read: '',
        unit_id: this.unit_id ?? '',
        comodato: false
      },
      true
    );

    this.availableMeterTypes.set([...this.meterTypes()]);
    this.availableMeterBrands.set([...this.meterBrands()]);
    this.typeBrandRelations.set([]);
    this.selectedReadingFormat.set('');
    this.currentMeterId.set(null);
    this.setUniqueValidators(null);
    this.imgEvidence.set('');
  }

  private applyMeterData(meter: any): void {
    if (!meter || !meter.id) {
      this.resetFormForCreate();
      return;
    }

    const statusId = this.resolveStatusId(meter);
    const typeId = this.resolveTypeId(meter);
    const brandId = this.resolveBrandId(meter);
    const relationId = this.toNumber(meter?.gas_meter_type_brand_id);
    const meterId = this.toNumber(meter?.id);

    this.patchForm({
      id: meter?.id ?? '',
      name: meter?.name ?? '',
      meter_code: meter?.meter_code ?? '',
      gas_meter_type_id: typeId,
      gas_meter_status_id: statusId,
      gas_meter_brand_id: brandId,
      gas_meter_type_brand_id: relationId,
      meter_serial: meter?.meter_serial ?? '',
      meter_location_latitude: meter?.meter_location_latitude ?? '',
      meter_location_longitude: meter?.meter_location_longitude ?? '',
      last_read: meter?.last_read !== null && meter?.last_read !== undefined ? `${meter?.last_read}` : '',
      unit_id: meter?.unit_id ?? this.unit_id ?? '',
      comodato: !!meter?.comodato
    });

    this.currentMeterId.set(meterId);
    this.setUniqueValidators(meterId);

    if (meterId) {
      this.store.getgasMeterImage(`${meterId}`).pipe(map((res) => res.data.attributes.image_url)).subscribe({
        next: (url) => this.imgEvidence.set(url),
        error: () => this.imgEvidence.set('')
      });
    }

    if (relationId && (!typeId || !brandId)) {
      this.store.getGasMeterTypeBrand(relationId).subscribe({
        next: (relation) => {
          const resolvedTypeId = this.toNumber(relation?.gas_meter_type_id?.id);
          const resolvedBrandId = this.toNumber(relation?.gas_meter_brand_id?.id);

          this.patchForm({
            gas_meter_type_id: resolvedTypeId,
            gas_meter_brand_id: resolvedBrandId,
            gas_meter_type_brand_id: this.toNumber(relation?.id)
          });

          this.syncTypeBrandCatalog(resolvedTypeId, resolvedBrandId, this.toNumber(relation?.id));
        },
        error: () => {
          this.syncTypeBrandCatalog(typeId, brandId, relationId);
        }
      });
      return;
    }

    this.syncTypeBrandCatalog(typeId, brandId, relationId);
  }

  private syncTypeBrandCatalog(typeId: number | null, brandId: number | null, preferredRelationId: number | null = null): void {
    this.updateReadingFormat(typeId);
    this.patchControlValue('gas_meter_type_brand_id', null);

    if (!typeId && !brandId) {
      this.relationRequestId += 1;
      this.typeBrandRelations.set([]);
      this.availableMeterTypes.set([...this.meterTypes()]);
      this.availableMeterBrands.set([...this.meterBrands()]);
      return;
    }

    const query: { gas_meter_type_id?: number; gas_meter_brand_id?: number } = {};
    if (typeId) {
      query.gas_meter_type_id = typeId;
    }
    if (brandId) {
      query.gas_meter_brand_id = brandId;
    }

    const requestId = ++this.relationRequestId;
    this.loadingTypeBrandRelations = true;

    this.store.getGasMeterTypeBrands(query).subscribe({
      next: (relations: GasMeterTypeBrandRelation[]) => {
        if (requestId !== this.relationRequestId) {
          return;
        }

        const safeRelations = Array.isArray(relations) ? relations : [];
        this.typeBrandRelations.set(safeRelations);

        const validTypeIds = new Set(
          safeRelations
            .map((relation) => this.toNumber(relation?.gas_meter_type_id?.id))
            .filter((id): id is number => id !== null)
        );

        const validBrandIds = new Set(
          safeRelations
            .map((relation) => this.toNumber(relation?.gas_meter_brand_id?.id))
            .filter((id): id is number => id !== null)
        );

        this.availableMeterTypes.set(
          brandId
            ? this.meterTypes().filter((type) => {
                const id = this.toNumber(type.id);
                return id !== null && validTypeIds.has(id);
              })
            : [...this.meterTypes()]
        );

        this.availableMeterBrands.set(
          typeId
            ? this.meterBrands().filter((brand) => {
                const id = this.toNumber(brand.id);
                return id !== null && validBrandIds.has(id);
              })
            : [...this.meterBrands()]
        );

        const currentTypeId = this.toNumber(this.ufc['gas_meter_type_id'].value);
        const currentBrandId = this.toNumber(this.ufc['gas_meter_brand_id'].value);

        if (currentTypeId && this.availableMeterTypes().every((type) => this.toNumber(type.id) !== currentTypeId)) {
          this.patchForm({ gas_meter_type_id: null });
          this.updateReadingFormat(null);
        }

        if (currentBrandId && this.availableMeterBrands().every((brand) => this.toNumber(brand.id) !== currentBrandId)) {
          this.patchForm({ gas_meter_brand_id: null });
        }

        const selectedTypeId = this.toNumber(this.ufc['gas_meter_type_id'].value);
        const selectedBrandId = this.toNumber(this.ufc['gas_meter_brand_id'].value);

        const matchedRelation = selectedTypeId && selectedBrandId
          ? safeRelations.find((relation) =>
              this.toNumber(relation?.gas_meter_type_id?.id) === selectedTypeId &&
              this.toNumber(relation?.gas_meter_brand_id?.id) === selectedBrandId
            ) ?? null
          : null;

        const preferredRelation = preferredRelationId
          ? safeRelations.find((relation) => this.toNumber(relation?.id) === preferredRelationId) ?? null
          : null;

        const relationToUse = matchedRelation ?? preferredRelation;
        this.patchControlValue('gas_meter_type_brand_id', relationToUse ? this.toNumber(relationToUse.id) : null);

        if (relationToUse?.gas_meter_type_id?.reading_format) {
          this.selectedReadingFormat.set(relationToUse.gas_meter_type_id.reading_format);
          this.ufc['last_read'].updateValueAndValidity({ emitEvent: false });
        }

        this.formatLastReadField(false);

        this.loadingTypeBrandRelations = false;
      },
      error: () => {
        if (requestId !== this.relationRequestId) {
          return;
        }

        this.loadingTypeBrandRelations = false;
        this.typeBrandRelations.set([]);
        this.patchControlValue('gas_meter_type_brand_id', null);
        this.toastr.error('No se pudo validar la combinación de tipo y marca.', 'Error');
      }
    });
  }

  private buildPayload(): any | null {
    const formData = this.meterForm.getRawValue();
    const gasMeterStatusId = this.toNumber(formData.gas_meter_status_id);
    const gasMeterTypeBrandId = this.toNumber(formData.gas_meter_type_brand_id);

    if (!gasMeterStatusId || !gasMeterTypeBrandId) {
      return null;
    }

    const maskedRead = this.formatReadWithMask(`${formData.last_read ?? ''}`.trim(), this.selectedReadingFormat());
    if (!maskedRead) {
      return null;
    }

    const payload: any = {
      name: `${formData.name ?? ''}`.trim(),
      meter_code: `${formData.meter_code ?? ''}`.trim(),
      meter_serial: `${formData.meter_serial ?? ''}`.trim(),
      meter_location_latitude: formData.meter_location_latitude ?? '',
      meter_location_longitude: formData.meter_location_longitude ?? '',
      last_read: maskedRead,
      unit_id: this.unit_id ?? formData.unit_id,
      comodato: !!formData.comodato,
      gas_meter_status_id: gasMeterStatusId,
      gas_meter_type_brand_id: gasMeterTypeBrandId
    };

    if (formData.id) {
      payload.id = formData.id;
    }

    return payload;
  }

  private patchForm(value: any, reset = false): void {
    this.suppressControlEvents = true;

    if (reset) {
      this.meterForm.reset(value, { emitEvent: false });
    } else {
      this.meterForm.patchValue(value, { emitEvent: false });
    }

    this.suppressControlEvents = false;
  }

  private patchControlValue(controlName: string, value: any): void {
    this.suppressControlEvents = true;
    this.ufc[controlName].patchValue(value, { emitEvent: false });
    this.ufc[controlName].updateValueAndValidity({ emitEvent: false });
    this.suppressControlEvents = false;
  }

  private updateReadingFormat(typeId: number | null): void {
    if (!typeId) {
      this.selectedReadingFormat.set('');
      this.ufc['last_read'].updateValueAndValidity({ emitEvent: false });
      return;
    }

    const type = this.meterTypes().find((item) => this.toNumber(item.id) === typeId);
    this.selectedReadingFormat.set(type?.reading_format ?? '');
    this.ufc['last_read'].updateValueAndValidity({ emitEvent: false });
    this.formatLastReadField(false);
  }

  private resolveTypeId(meter: any): number | null {
    const directId = this.toNumber(meter?.gas_meter_type_id ?? meter?.meter_type_id ?? meter?.meter_type);
    if (directId && this.meterTypes().some((type) => this.toNumber(type.id) === directId)) {
      return directId;
    }

    const lookup = this.normalizeCatalogKey(meter?.meter_type);
    if (!lookup) {
      return null;
    }

    const match = this.meterTypes().find((type) =>
      this.normalizeCatalogKey(type.name) === lookup ||
      this.normalizeCatalogKey(type.description) === lookup
    );

    return this.toNumber(match?.id);
  }

  private resolveBrandId(meter: any): number | null {
    const directId = this.toNumber(meter?.gas_meter_brand_id ?? meter?.meter_brand_id ?? meter?.meter_brand);
    if (directId && this.meterBrands().some((brand) => this.toNumber(brand.id) === directId)) {
      return directId;
    }

    const lookup = this.normalizeCatalogKey(meter?.meter_brand);
    if (!lookup) {
      return null;
    }

    const match = this.meterBrands().find((brand) =>
      this.normalizeCatalogKey(brand.name) === lookup ||
      this.normalizeCatalogKey(brand.description) === lookup
    );

    return this.toNumber(match?.id);
  }

  private resolveStatusId(meter: any): number | null {
    const directId = this.toNumber(meter?.gas_meter_status_id ?? meter?.meter_status_id ?? meter?.meter_status);
    if (directId && this.meterStatuses().some((status) => this.toNumber(status.id) === directId)) {
      return directId;
    }

    const lookup = this.normalizeCatalogKey(meter?.meter_status);
    if (!lookup) {
      return null;
    }

    const match = this.meterStatuses().find((status) =>
      this.normalizeCatalogKey(status.name) === lookup ||
      this.normalizeCatalogKey(status.description) === lookup
    );

    return this.toNumber(match?.id);
  }

  private normalizeCatalogKey(value: any): string {
    return `${value ?? ''}`.trim().toLowerCase().replace(/[\s_-]+/g, '');
  }

  private lastReadFormatValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const rawValue = `${control.value ?? ''}`.trim();
      if (!rawValue) {
        return null;
      }

      const format = this.selectedReadingFormat();
      const parsedFormat = this.parseReadingFormat(format);
      if (!parsedFormat) {
        return null;
      }

      const normalized = rawValue.replace(',', '.');
      if (!/^\d+(\.\d+)?$/.test(normalized)) {
        return { readingFormat: true };
      }

      const [integerPart = '', decimalPart = ''] = normalized.split('.');
      if (integerPart.length > parsedFormat.integerDigits) {
        return { readingFormat: true };
      }

      if (parsedFormat.decimalDigits === 0 && decimalPart.length > 0) {
        return { readingFormat: true };
      }

      if (decimalPart.length > parsedFormat.decimalDigits) {
        return { readingFormat: true };
      }

      return null;
    };
  }

  private parseReadingFormat(format?: string | null): { integerDigits: number; decimalDigits: number } | null {
    if (!format) {
      return null;
    }

    const [integerPart, decimalPart] = format.split(',');
    const integerDigits = Number(integerPart);
    const decimalDigits = Number(decimalPart);

    if (!Number.isFinite(integerDigits) || !Number.isFinite(decimalDigits)) {
      return null;
    }

    if (integerDigits < 1 || decimalDigits < 0) {
      return null;
    }

    return { integerDigits, decimalDigits };
  }

  private formatReadWithMask(rawValue: string, format?: string | null): string | null {
    const value = `${rawValue ?? ''}`.trim();
    if (!value) {
      return '';
    }

    const parsedFormat = this.parseReadingFormat(format);
    if (!parsedFormat) {
      return value.replace('.', ',');
    }

    const normalizedValue = value.replace(',', '.');
    if (!/^\d+(\.\d+)?$/.test(normalizedValue)) {
      return null;
    }

    const [integerPart = '', decimalPart = ''] = normalizedValue.split('.');
    if (integerPart.length > parsedFormat.integerDigits || decimalPart.length > parsedFormat.decimalDigits) {
      return null;
    }

    const formattedIntegerPart = integerPart.padStart(parsedFormat.integerDigits, '0');
    if (parsedFormat.decimalDigits === 0) {
      return formattedIntegerPart;
    }

    const formattedDecimalPart = decimalPart.padEnd(parsedFormat.decimalDigits, '0');
    return `${formattedIntegerPart}.${formattedDecimalPart}`;
  }
}
